import { Root, Thumb } from '@radix-ui/react-switch';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

export type SwitchProps = ComponentPropsWithoutRef<typeof Root>;

/**
 * The status toggle W7 mandates on a toggleable status cell — wired straight to
 * the update mutation, permission-gated by the caller.
 *
 * The thumb travels on `translate-x` under an RTL-aware sign rather than a
 * physical `left`: `rtl:-translate-x-4` mirrors the motion so the switch reads
 * "off → on" in the direction the operator reads.
 */
export const Switch = forwardRef<ElementRef<typeof Root>, SwitchProps>(function Switch(
  { className, ...props },
  ref,
) {
  return (
    <Root
      ref={ref}
      className={cn(
        'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-fast ease-standard',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
        className,
      )}
      {...props}
    >
      <Thumb
        className={cn(
          'pointer-events-none block h-5 w-5 rounded-full bg-card shadow-sm ring-0 transition-transform duration-fast ease-standard',
          'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
          'rtl:data-[state=checked]:-translate-x-5',
        )}
      />
    </Root>
  );
});
